/**
 * Every user-facing string, in US English — the shipped default and the source
 * of truth for which keys exist. Other languages are rows in the translations
 * table, seeded from packages/backend/prisma/seed-data/i18n/<locale>.json
 * (src/i18n/seed-coverage.spec.ts keeps those in step with this file).
 *
 * Never hardcode UI text in a component: add it here, then add its
 * translation to every seed file.
 */
export default {
  common: {
    appName: 'Elfie Care Intelligence',
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    somethingWentWrong: 'Something went wrong. Please try again.',
  },
  nav: {
    home: 'Home',
    settings: 'Settings',
    menu: 'Menu',
    userManagement: 'Users',
    languageManagement: 'Languages',
    workspaceManagement: 'Workspaces',
    intelligenceSettings: 'Intelligence settings',
    overview: 'Overview',
    opportunities: 'Opportunities',
    objectives: 'Objectives',
    reports: 'Reports',
    attention: 'My Attention',
    transparency: 'Data & privacy',
    logOut: 'Log out',
  },
  home: {
    greeting: {
      nightOwl: 'Up late, {name}?',
      earlyBird: 'An early start, {name}',
      morning: 'Good morning, {name}',
      afternoon: 'Good afternoon, {name}',
      evening: 'Good evening, {name}',
      lateNight: 'Working late, {name}?',
    },
    subtitle: 'Welcome to Elfie Care Intelligence.',
    workspaces: {
      title: 'Your workspaces',
      viewAsBlocked: 'Workspaces are hidden while you view the product as someone else.',
      failed: 'We couldn’t load your workspaces. Please refresh the page.',
      empty: {
        title: 'No workspace yet',
        body: 'Your workspace will appear here once Elfie turns on Intelligence for it and gives you access. If you expected to see one, contact your Elfie team.',
      },
      /** P1 §3.1: this Home stands in for ElfieCare's until the move (DECISIONS.md #17). */
      enterIntelligence: 'Enter Intelligence',
    },
    /** Why a link to a workspace page landed here instead (P1 §2.1), by the API's error code. */
    refused: {
      WORKSPACE_NOT_FOUND: 'That link goes to a workspace you’re not a member of, or one that no longer exists.',
      INTELLIGENCE_NOT_ENABLED: 'That link goes to a workspace where Intelligence isn’t turned on yet.',
      INTELLIGENCE_ACCESS_REQUIRED: 'You don’t have access to that page yet. The workspace Owner can give it to you.',
      WORKSPACE_VIEW_AS_BLOCKED: 'That page is hidden while you view the product as someone else.',
      ATTESTATION_IN_DEMONSTRATION: 'Demonstration workspaces don’t have a workspace profile, because their data is made up for demos.',
      CONTEXT_DECLARER_REQUIRED: 'Only the workspace’s Owners and Admins can open the workspace profile.',
    },
  },
  profile: {
    editTitle: 'Edit my profile',
    pictureAdd: 'Add a picture',
    pictureReplace: 'Replace picture',
    pictureRemove: 'Remove',
    pictureHint: 'PNG, JPEG or WebP, up to 512 KB. Without a picture, we show your initials.',
    roleManagedByAdmin: 'Your role is set by an admin.',
    saved: 'Your profile is saved.',
    language: 'Language',
    switchToDark: 'Switch to dark mode',
    switchToLight: 'Switch to light mode',
    copyBuild: 'Copy the build identifier',
    buildCopied: 'Copied',
  },
  viewAs: {
    menu: 'View as user',
    title: 'View the product as another user',
    description:
      'See the product exactly as they do, with their role and settings. You can look, but not change anything. Admins can’t be viewed as.',
    pick: 'User',
    start: 'View as this user',
    banner: 'Viewing as {name} · {role} · read only',
    exit: 'Exit view',
    noCandidates: 'There’s no active user or manager to view as.',
  },
  workspace: {
    /** P1 §4 and S0 §55.3: the wording of the marker is the spec's. */
    demonstrationMarker: 'Demonstration data',
    demonstrationExplainer: 'This workspace uses made-up data for demonstrations. No real patients or clinic are shown.',
    roles: {
      OWNER: 'Owner',
      ADMIN: 'Admin',
      MEMBER: 'Member',
    },
    errors: {
      WORKSPACE_NOT_FOUND: 'This workspace doesn’t exist, or you’re not a member of it.',
      INTELLIGENCE_NOT_ENABLED: 'Intelligence isn’t turned on for this workspace yet.',
      INTELLIGENCE_ACCESS_REQUIRED: 'You don’t have access to this page yet. The workspace Owner can give it to you.',
      WORKSPACE_VIEW_AS_BLOCKED: 'Workspaces are hidden while you view the product as someone else.',
      ATTESTATION_IN_DEMONSTRATION: 'A demonstration workspace has no workspace profile: its data is made up for demos.',
      CONTEXT_DECLARER_REQUIRED: 'Only the workspace’s Owners and Admins can change the workspace profile.',
    },
    backHome: 'Back to Home',
    settings: {
      title: 'Intelligence settings',
      tabs: {
        members: 'Members & access',
        clinicProfile: 'Workspace profile',
        myFunction: 'My function',
        transparency: 'Data & privacy',
      },
      members: 'Members',
      membersDescription:
        'Everyone in this workspace in ElfieCare. To add or remove someone, do it in ElfieCare and this list will update.',
      syncedAt: 'Updated from ElfieCare on {date}',
      name: 'Name',
      email: 'Email',
      role: 'Role in ElfieCare',
      empty: 'No members yet.',
      intelligence: 'Intelligence access',
      seatsDescription:
        'Choose who on your team can use Intelligence. As the Owner, you always can. Each change is logged under Data & privacy.',
      byOwnership: 'Always, as Owner',
      seatLabel: 'Intelligence access for {name}',
      grantedBy: 'Given by {name} on {date}',
      revokedOn: 'Removed on {date}',
      granted: '{name} can now use Intelligence.',
      revoked: '{name} can no longer use Intelligence.',
    },
  },
  /** P2: the Management Overview, and Home's entry section to it (P1 §3.1.1). */
  overview: {
    subtitle: 'Here’s how {workspace} is doing.',
    period: {
      label: 'Period',
      TODAY: 'Today',
      LAST_7_DAYS: 'Last 7 days',
      LAST_30_DAYS: 'Last 30 days',
      CUSTOM: 'Custom range',
      chooseRange: 'Choose a start and end date.',
      invalid: 'Choose days up to today, with the start before the end and no more than a year apart.',
      comparedWith: 'Compared with {range}',
      notCompared: 'Not compared with {range}: your records don’t cover all of it',
      timezone: 'Days run midnight to midnight, {zone} time. You can change your workspace’s time zone in ElfieCare settings.',
      timezoneUnknown: 'Days run midnight to midnight, UTC time. You can set your workspace’s time zone in ElfieCare settings.',
    },
    dataHealth: {
      /** The badge, whole, so each language can make the state agree with "data". */
      badge: {
        DEGRADED: 'Data delayed',
        BLOCKED: 'Data unavailable',
      },
      asOf: 'Updated {time}',
      NOT_CONNECTED: 'Intelligence isn’t connected to ElfieCare yet, so there’s nothing to show.',
      UNREACHABLE: 'We can’t reach ElfieCare right now. Your figures will come back once we can.',
      NOT_LOADED: 'The demonstration data is being prepared. It will appear within a minute.',
    },
    snapshot: {
      title: 'Key figures',
      notMeasurable: 'Not available yet',
      /** In place of a figure for a period before the workspace's records begin (S0 §21.5 rule 4). */
      notRecorded: 'Not recorded',
      stale: 'Last updated {time}, so it may be a little behind.',
      staleUnknown: 'We can’t tell when this was last updated, so it may be behind.',
      vs: 'vs {range}',
      noComparison: 'No figures for {range} to compare with',
      better: 'Better',
      worse: 'Worse',
      definition: 'What this counts',
      /** The "!" beside a figure that is incomplete or behind: what limits it (P2 §3.8). */
      limits: 'Why this figure may be incomplete',
    },
    metrics: {
      appointments: {
        name: 'Appointments',
        definition: 'Appointments booked for a day in this period. Appointments cancelled by staff, or cancelled with their visit, aren’t counted.',
      },
      visitsCompleted: {
        name: 'Visits completed',
        definition: 'Visits marked as completed in this period.',
      },
      patientsSeen: {
        name: 'Patients seen',
        definition: 'Patients with at least one completed visit in this period. Each patient counts once.',
      },
      waitingTime: {
        name: 'Median waiting time',
        definition:
          'How long patients waited for the clinician who saw them, from being assigned to them in the waiting room until their visit started. If a patient was handed over, for example from the nurse who took them in to the doctor, the wait counts from the handover. Half waited less than this, half waited longer.',
      },
      activeMembers: {
        name: 'Active members',
        definition:
          'Members who did at least one thing in ElfieCare in this period: booked an appointment, started or completed a visit, created a patient folder or wrote a patient note.',
      },
    },
    units: {
      minutes: '{value} min',
    },
    notes: {
      NOT_CONNECTED: 'Needs the connection to ElfieCare.',
      UNREACHABLE: 'ElfieCare can’t be reached right now.',
      NOT_LOADED: 'The demonstration data is being prepared.',
      /** Plural by `measured`: none | one | more. */
      TOO_FEW:
        'No waits were measured in this period. We need at least {minimum} to give a reliable figure. | Only 1 wait was measured in this period. We need at least {minimum} to give a reliable figure. | Only {measured} waits were measured in this period. We need at least {minimum} to give a reliable figure.',
      WAITING_ROOM_VISITS_ONLY: 'Based on {measured} of {of} visits: the ones that went through the waiting room.',
      /** No longer sent (DECISIONS.md #37): the wait for whoever sees the patient is the measure, not a limit. Opportunities stored before it still carry it. */
      LATEST_ASSIGNMENT: 'The wait counts from the last handover, for example from the nurse who took the patient in to the doctor: the time spent waiting for the clinician who sees them.',
      /** S0 §21.5 rule 4: the period starts before the workspace's records. `day` is their first. */
      NOT_RECORDED_BEFORE: 'Counted from {day}, when your records start. The days before aren’t recorded.',
      NOT_RECORDED: 'Nothing is recorded in this period. Your records start on {day}.',
      NOT_RECORDED_YET: 'Nothing is recorded yet.',
    },
    /** P2 §2: the Overview's two perspectives, kept in the address as `?perspective=`. */
    perspective: {
      label: 'What to look at',
      OPERATIONS: 'Operations',
      /** Dao, 2 Oct 2026: on screen, Care quality, not Care Quality & Continuity. */
      CONTINUITY: 'Care quality',
    },
    /** P2 §2.2, DECISIONS.md #38, #39: the chronic patients' journey, in five steps never added up. */
    continuity: {
      asOf: {
        now: 'Figures as of now, compared with the same time on {day}',
        end: 'Figures as of the end of {day}, compared with the end of {before}',
      },
      journey: {
        title: 'Your chronic patients’ journey',
        subtitle: 'Patients with diabetes, high blood pressure or kidney disease, from the conditions recorded in ElfieCare.',
        infoLabel: 'How the journey is counted',
        info: 'Each step is counted on its own and never added up into one score: more data doesn’t mean better care, and a gap in your records doesn’t mean a gap in care.',
        step: 'Step {n}',
      },
      stages: {
        COHORT_IDENTIFICATION: 'Patients needing ongoing care',
        CAREPLAN_ACTIVATION: 'Patients with a next step',
        PATIENT_CONNECTION: 'Connected between visits',
        LONGITUDINAL_CONTINUITY: 'Followed over time',
        CARE_LOOP_CLOSURE: 'Care loops closed',
      },
      measures: {
        cohortId: {
          name: 'Patients needing ongoing care',
          definition:
            'Patients seen in the last year with diabetes, high blood pressure or kidney disease recorded in ElfieCare (ICD-10 E10, E11, I10 or N18), and their share of everyone seen that year.',
        },
        riskProfiles: {
          name: 'Complete risk profile',
          definition:
            'Of those patients, the ones with a date of birth, a current medicine, and in the last year a reading for each of their conditions: an HbA1c for diabetes, a blood pressure for high blood pressure, a lab result for kidney disease.',
        },
        careplanAct: {
          name: 'Patients with a next step',
          definition:
            'Of chronic patients seen in the last 90 days, the ones with a later appointment booked or connected to the Elfie app. Lab orders aren’t recorded in ElfieCare, so they don’t count here.',
        },
        scheduledFollowup: {
          name: 'Follow-up booked within 90 days',
          definition: 'Of chronic patients seen in the last 90 days, the ones with an appointment booked within 90 days of their last visit.',
        },
        ptConnect: {
          name: 'Connected between visits',
          definition: 'Of chronic patients invited to the Elfie app, the ones who accepted and sent at least one reading from it in the last 30 days.',
        },
        interencounterReach: {
          name: 'Sending readings regularly',
          definition: 'Of chronic patients connected to the Elfie app, the ones who sent at least four readings from it in the last 30 days.',
        },
        retention90d: {
          name: 'Followed over time',
          definition:
            'Of chronic patients registered for at least 90 days, the ones with a visit or a reading, in the clinic or from the app, in at least two of the last three 30-day periods.',
        },
        densePatientMonths: {
          name: 'Fully documented months',
          definition:
            'Over the last three 30-day periods, each one in which a chronic patient had a current medicine, a visit or a reading, and by its end a next appointment booked or a connection to the Elfie app.',
        },
        loopClosure: {
          name: 'Care loops closed',
          definition:
            'Abnormal results from the last 90 days, at least 14 days old, followed within 14 days by a visit or a clinician’s note; and diabetic patients with no HbA1c in the six months before those 90 days who have had one since.',
        },
        abnormalReview: {
          name: 'Abnormal results followed up',
          definition:
            'Chronic patients’ abnormal results from the last 90 days, at least 14 days old: a lab value marked abnormal, or a blood pressure of 140/90 or more, in the clinic or from the app. Followed up means a visit or a clinician’s note within 14 days.',
        },
        hba1c: {
          name: 'HbA1c checked',
          definition: 'Diabetic patients with an HbA1c recorded in the last six months, as the ADA Standards of Care ask.',
        },
        bpControl: {
          name: 'Blood pressure under control',
          definition:
            'Patients with high blood pressure whose latest reading in the last year is below 140/90, or below 130/80 if they also have diabetes or kidney disease (AHA/ACC). Counted among those with a reading.',
        },
        kdigoRenal: {
          name: 'Kidney checks',
          definition: 'Patients with kidney disease who had an eGFR and a urine albumin test as often as KDIGO advises.',
        },
        footExam: {
          name: 'Diabetic foot exam',
          definition: 'Diabetic patients who had a full foot exam in the last year, as the ADA Standards of Care ask.',
        },
      },
      /** Under a rate: what it is a share of. */
      of: {
        PATIENTS: '{numerator} of {denominator} patients',
        RESULTS: '{numerator} of {denominator} results',
        CARE_LOOPS: '{numerator} of {denominator} care loops',
      },
      /** Step 1's figure is the patients themselves, with their share of everyone seen. */
      cohortShare: '{rate} of the {denominator} patients seen in the last year',
      points: '{value} pts',
      vs: 'vs {day}',
      noComparison: 'Nothing to compare with yet',
      notMeasurable: 'Not measurable yet',
      notes: {
        RECORDS_NOT_CONNECTED: 'ElfieCare’s clinical records aren’t connected to Intelligence yet.',
        /** `inputs` is a list of what is missing, e.g. "diagnoses coded with ICD-10 and patients invited to the Elfie app". */
        INPUTS_NOT_RECORDED: 'Needs {inputs}.',
        /** Never how many: a group under the minimum is hidden without saying whether nobody or a few (S0 §17.1, DECISIONS.md #20). */
        TOO_FEW: 'Too few to give a reliable figure yet. We need at least {minimum}.',
        LAB_ORDERS_NOT_RECORDED: 'Lab orders aren’t recorded in ElfieCare, so a test still to be done doesn’t count as a next step.',
        MEASURED_OF: 'Based on the {measured} of {of} patients with a blood pressure recorded in the last year.',
      },
      getIt: 'See how to get it',
      coverage: {
        title: 'What would complete the picture',
        subtitle: 'What we can’t see yet, and the two ways to get it',
        intro: 'Each of these is missing from your records today. Record it in ElfieCare, or connect the system that already holds it.',
        notConnectedTitle: 'Your clinical records aren’t connected yet',
        notConnected:
          'The journey reads the conditions, medicines and readings recorded in ElfieCare. Elfie is connecting them to Intelligence for your workspace, and your figures will appear here once that’s done.',
      },
      guidelines: {
        title: 'Clinical guidelines',
        subtitle: 'How often chronic patients get the checks their guideline asks for',
        infoLabel: 'What these figures can and can’t tell you',
        info: 'A check done but recorded somewhere else doesn’t show here: a missing record isn’t missing care.',
        sources: {
          ADA: 'ADA',
          AHA_ACC: 'AHA/ACC',
          KDIGO: 'KDIGO',
          WORKFLOW: 'Note checklist',
        },
        needs: 'See what’s needed',
      },
      /** Elfie Pulse's line for a journey step that moved, per step's lead measure: went up | went down. */
      moves: {
        /** Step 1 asks whether the clinic knows who needs ongoing care: a move in what is recorded, not in how ill patients are. */
        cohortId: {
          UP: 'More of the patients you see have a long-term condition recorded: from {from} to {to} ({change}).',
          DOWN: 'Fewer of the patients you see have a long-term condition recorded: from {from} to {to} ({change}).',
        },
        careplanAct: {
          UP: 'More chronic patients have a next step: from {from} to {to} ({change}).',
          DOWN: 'Fewer chronic patients have a next step: from {from} to {to} ({change}).',
        },
        ptConnect: {
          UP: 'More patients are connected between visits: from {from} to {to} ({change}).',
          DOWN: 'Fewer patients are connected between visits: from {from} to {to} ({change}).',
        },
        retention90d: {
          UP: 'More chronic patients are followed over time: from {from} to {to} ({change}).',
          DOWN: 'Fewer chronic patients are followed over time: from {from} to {to} ({change}).',
        },
        loopClosure: {
          UP: 'More care loops are being closed: from {from} to {to} ({change}).',
          DOWN: 'Fewer care loops are being closed: from {from} to {to} ({change}).',
        },
      },
    },
    utilization: {
      title: 'ElfieCare operations',
      description: '{workspace}’s operations in ElfieCare',
      /** Dao, 2 Oct 2026: read 7 days at a time, apart from the page's period, named by their days (DECISIONS.md #44). */
      week: {
        label: 'Week shown',
        previous: 'Previous week',
        next: 'Next week',
      },
      activity: {
        title: 'Activity across the week',
        subtitle: 'Everything recorded in ElfieCare, by day and hour ({zone} time).',
        /** Plural by `count`: none | one | more. */
        total: 'Nothing recorded in these 7 days. | 1 action recorded in these 7 days. | {count} actions recorded in these 7 days.',
        empty: 'Nothing was recorded in ElfieCare in these 7 days.',
        /** One square of the heat map, read aloud and shown on hover. Plural by `count`. */
        cell: '{day}, {from}–{to}: no actions | {day}, {from}–{to}: 1 action | {day}, {from}–{to}: {count} actions',
        hint: 'Point at or tab to a square to see its count.',
        legendNone: 'None',
        legendFewer: 'Fewer',
        legendMore: 'More',
        definition:
          'An action is anything recorded in ElfieCare: booking an appointment, starting or completing a visit, creating a patient folder or writing a note. A visit counts once when it starts and once when it ends.',
      },
      topMembers: {
        title: 'Most active members',
        subtitle: 'Most patient encounters in these 7 days',
        empty: 'No member recorded {minimum} or more encounters in these 7 days.',
        rule: 'The {limit} members with the most encounters in these 7 days, each with at least {minimum}.',
      },
      /** DECISIONS.md #33: the clinic's own departments, from its own systems once connected. */
      byDepartment: {
        title: 'Activities by department',
        subtitle: 'Encounters in these 7 days',
        notAssigned: 'No department',
        notConnected: 'Departments aren’t connected yet',
        connect:
          'No department data connected.',
      },
      encounter:
        'An encounter is an appointment booked, or a visit started or completed, in ElfieCare. Each counts once, for the person who did it.',
      adoption:
        'This shows how much work is recorded in ElfieCare, not how well anyone works. A low figure usually means some work is still recorded elsewhere.',
      /** ElfieCare's own labels for `User.professionalRole`, under each top member's name. */
      roles: {
        SPECIALIST: 'Specialist / Consultant',
        GENERAL_PRACTITIONER: 'General Practitioner (GP)',
        PROFESSOR_ACADEMIC_MD: 'Professor / Academic MD',
        RESIDENT_FELLOW: 'Resident / Fellow',
        NURSE_PRACTITIONER: 'Nurse / Nurse Practitioner',
        MEDICAL_ASSISTANT_SCRIBE: 'Medical Assistant / Scribe',
        SECRETARY_ADMINISTRATIVE_STAFF: 'Secretary / Administrative Staff',
        MEDICAL_NURSING_STUDENT: 'Medical / Nursing Student',
      },
    },
    whatChanged: {
      /** A sub-section of Key figures (Dao, 2 Oct 2026). */
      title: 'Change summary',
      /** A whole sentence per metric and direction, so each language can make the verb agree. */
      appointments: {
        UP: 'Appointments went up, from {from} to {to} ({change}).',
        DOWN: 'Appointments went down, from {from} to {to} ({change}).',
        STEADY: 'Appointments stayed about the same, from {from} to {to} ({change}).',
      },
      visitsCompleted: {
        UP: 'Visits completed went up, from {from} to {to} ({change}).',
        DOWN: 'Visits completed went down, from {from} to {to} ({change}).',
        STEADY: 'Visits completed stayed about the same, from {from} to {to} ({change}).',
      },
      patientsSeen: {
        UP: 'Patients seen went up, from {from} to {to} ({change}).',
        DOWN: 'Patients seen went down, from {from} to {to} ({change}).',
        STEADY: 'Patients seen stayed about the same, from {from} to {to} ({change}).',
      },
      waitingTime: {
        UP: 'Median waiting time went up, from {from} to {to} ({change}).',
        DOWN: 'Median waiting time went down, from {from} to {to} ({change}).',
        STEADY: 'Median waiting time stayed about the same, from {from} to {to} ({change}).',
      },
      activeMembers: {
        UP: 'Active members went up, from {from} to {to} ({change}).',
        DOWN: 'Active members went down, from {from} to {to} ({change}).',
        STEADY: 'Active members stayed about the same, from {from} to {to} ({change}).',
      },
      empty: 'Nothing to compare yet: there isn’t enough data in both periods.',
      /** DECISIONS.md #34: the period before is not all recorded; records begin in it, during this period, or after it. */
      notRecorded: 'Nothing to compare yet: your records start on {day}, partway through the earlier period.',
      notRecordedDuring: 'Nothing to compare yet: your records start on {day}, partway through this period.',
      notRecordedAfter: 'Nothing to compare: your records start on {day}, after this period ended.',
    },
    home: {
      period: 'Last 7 days',
      /** S0 §21.5 rule 4: under the period, when records begin partway through it. `day` is their first. */
      recordedFrom: 'Recorded from {day}',
      failed: 'We couldn’t load these figures.',
    },
    /** P2 Zone 4. */
    objectives: {
      /** Performance, for short (Dao, 2 Oct 2026). */
      title: 'Performance',
      all: 'All objectives',
      period: 'Objectives keep their own time frame, whatever period you choose above: the last 30 days, or a care journey share as it stands.',
      /** Above the Objectives page's own Create objective, which it opens (DECISIONS.md #44). */
      empty: 'You’re not tracking any objectives yet.',
      failed: 'We couldn’t load your objectives.',
    },
    /** P2 Zone 5. */
    initiatives: {
      title: 'Current initiatives',
      empty: 'No follow-ups in progress.',
      failed: 'We couldn’t load current initiatives.',
      /** What each card follows up, under its name. */
      from: {
        OPPORTUNITY: 'From an opportunity',
        OBJECTIVE: 'From an objective',
        REPORT: 'From a report',
      },
      followUps: 'Follow-ups',
      /** The card's badge. Plural by `total`. */
      completed: 'No follow-ups | {completed} of 1 completed | {completed} of {total} completed',
    },
  },
  /** P3: the Opportunities feed and the Investigation view, in words for clinic leaders (DECISIONS.md #36). */
  opportunities: {
    title: 'Opportunities',
    subtitle: 'Things in your operations and your patients’ care that may need a closer look.',
    attention: 'Nothing needs your attention | 1 opportunity may need your attention | {n} opportunities may need your attention',
    ranked: 'Most important first',
    empty: 'Nothing stood out in this period.',
    emptyFiltered: 'No opportunities match these filters.',
    howFound:
      'We compare each full week, Monday to Sunday, with the {baselineWeeks} weeks before waits began to rise. A week counts as longer when its median wait is at least {percent} and {minutes} longer than usual, with at least {visits} visits measured that week. It closes on its own after {resolveWeeks} weeks back at the usual level. This is a fixed rule, not AI.',
    filters: {
      status: 'Status',
      domain: 'Domain',
      priority: 'Priority',
    },
    statusFilter: {
      ALL: 'All',
      ACTIVE: 'Active',
      RESOLVED: 'Resolved',
      DISMISSED: 'Dismissed',
    },
    domainFilter: {
      ALL: 'All',
      OPERATIONS: 'Operations',
      CARE_QUALITY: 'Care quality',
    },
    priorityFilter: {
      ALL: 'All',
      HIGH: 'High',
      MEDIUM: 'Medium',
      LOW: 'Low',
    },
    priority: {
      HIGH: 'High',
      MEDIUM: 'Medium',
      LOW: 'Low',
    },
    status: {
      NEW: 'New',
      REOPENED: 'Reopened',
      RESOLVED: 'Resolved',
      DISMISSED: 'Dismissed',
    },
    domain: {
      PATIENT_FLOW: 'Patient flow',
      CARE_CONTINUITY: 'Care journey',
    },
    metrics: {
      'METRIC-OP-WAITING-TIME': {
        title: 'Patients are waiting longer',
        headline: 'The median wait rose from {from} to {to}.',
      },
      /** DECISIONS.md #45. */
      'METRIC-CONT-CAREPLAN-ACT': {
        title: 'Fewer chronic patients have a next step',
        headline: 'The share with a next step fell from {from} to {to}.',
      },
    },
    affected: 'No visits measured in the longer-wait weeks | 1 visit measured in the longer-wait weeks | {n} visits measured in the longer-wait weeks',
    readiness: {
      READY: 'Data: complete',
      PARTIAL: 'Data: partial',
      STALE: 'Data: may be out of date',
      INSUFFICIENT: 'Data: too little to rely on',
      UNAVAILABLE: 'Data: unavailable',
    },
    whySurfacedLine: 'Why it’s here: {reason}',
    rationale: {
      weeksInARow: 'Waits have been longer than usual for {weeks} weeks in a row.',
      weeksInARowUpTo: 'Waits were longer than usual for {weeks} weeks in a row, up to the week of {week}.',
      aboveUsual: 'Waits were {percent} longer than usual last week.',
      aboveUsualInWeek: 'Waits were {percent} longer than usual in the week of {week}.',
      worseLastWeek: 'Waits were longer than usual last week.',
      worseInWeek: 'Waits were longer than usual in the week of {week}.',
      /** The next-step rule's (DECISIONS.md #45). `points` is written "3.2 pts". */
      fewerWeeksInARow: 'Fewer patients than usual have had a next step for {weeks} weeks in a row.',
      fewerWeeksInARowUpTo: 'Fewer patients than usual had a next step for {weeks} weeks in a row, up to the week of {week}.',
      belowUsual: 'The share with a next step was {points} below usual last week.',
      belowUsualInWeek: 'The share with a next step was {points} below usual in the week of {week}.',
      fewerLastWeek: 'Fewer patients than usual had a next step last week.',
      fewerInWeek: 'Fewer patients than usual had a next step in the week of {week}.',
    },
    /** An opportunity's figures naming the week they are from (Dao, 1 Oct 2026), as Elfie Pulse and Elfie's bubble say them. */
    inWeek: {
      aboveUsual: 'In the week of {week}, the median wait was {to}, {percent} longer than the usual {from}.',
      weeksInARow: 'The median wait has been above the usual {from} for {weeks} weeks in a row. In the week of {week}, it was {to}.',
      worse: 'In the week of {week}, the median wait was {to}, longer than the usual {from}.',
      belowUsual: 'In the week of {week}, {to} of chronic patients seen in the last 90 days had a next step, {points} below the usual {from}.',
      fewerWeeksInARow: 'The share of chronic patients with a next step has been below the usual {from} for {weeks} weeks in a row. In the week of {week}, it was {to}.',
      fewer: 'In the week of {week}, {to} of chronic patients seen in the last 90 days had a next step, below the usual {from}.',
    },
    hourRange: '{from}–{to}',
    concentration: {
      HOUR: 'Most of the increase is between {from} and {to}.',
      DAY: {
        '1': 'Most of the increase is on Mondays.',
        '2': 'Most of the increase is on Tuesdays.',
        '3': 'Most of the increase is on Wednesdays.',
        '4': 'Most of the increase is on Thursdays.',
        '5': 'Most of the increase is on Fridays.',
        '6': 'Most of the increase is on Saturdays.',
        '7': 'Most of the increase is on Sundays.',
      },
      ROLE: {
        SPECIALIST: 'Most of the increase is in visits started by specialists.',
        GENERAL_PRACTITIONER: 'Most of the increase is in visits started by general practitioners.',
        PROFESSOR_ACADEMIC_MD: 'Most of the increase is in visits started by professors and academic doctors.',
        RESIDENT_FELLOW: 'Most of the increase is in visits started by residents and fellows.',
        NURSE_PRACTITIONER: 'Most of the increase is in visits started by nurse practitioners.',
        MEDICAL_ASSISTANT_SCRIBE: 'Most of the increase is in visits started by medical assistants and scribes.',
        SECRETARY_ADMINISTRATIVE_STAFF: 'Most of the increase is in visits started by secretaries and administrative staff.',
        MEDICAL_NURSING_STUDENT: 'Most of the increase is in visits started by medical and nursing students.',
        UNKNOWN: 'Most of the increase is in visits started by people with no role on record.',
      },
      VISIT_TYPE: {
        FIRST_VISIT: 'Most of the increase is in first visits.',
        RETURNING_PATIENT: 'Most of the increase is in returning patients’ visits.',
        WALK_IN: 'Most of the increase is in walk-ins.',
      },
      figures: 'Waits there: {rise}, compared with {overall} overall.',
    },
    roles: {
      SPECIALIST: 'Specialists',
      GENERAL_PRACTITIONER: 'General practitioners',
      PROFESSOR_ACADEMIC_MD: 'Professors and academic doctors',
      RESIDENT_FELLOW: 'Residents and fellows',
      NURSE_PRACTITIONER: 'Nurse practitioners',
      MEDICAL_ASSISTANT_SCRIBE: 'Medical assistants and scribes',
      SECRETARY_ADMINISTRATIVE_STAFF: 'Secretaries and administrative staff',
      MEDICAL_NURSING_STUDENT: 'Medical and nursing students',
      UNKNOWN: 'No role on record',
    },
    visitTypes: {
      FIRST_VISIT: 'First visits',
      RETURNING_PATIENT: 'Returning patients',
      WALK_IN: 'Walk-ins',
    },
    actions: {
      investigate: 'Investigate',
      dismiss: 'Dismiss',
    },
    /** Whether anyone has been given a follow-up on it (Dao, 1 Oct 2026). Plural by `n`: none | one | more. */
    assignment: 'Open · nobody assigned yet | Assigned · 1 follow-up | Assigned · {n} follow-ups',
    resolved: 'Resolved on {date}: waits were back to normal.',
    dismissedOn: 'Dismissed on {date}: {reason}',
    reasonWithNote: '{reason} (“{note}”)',
    recurrence: 'This came back after an earlier opportunity about the same problem was dismissed.',
    dismiss: {
      title: 'Dismiss this opportunity',
      body: 'It stays in your records and won’t come back while this problem lasts. If the problem goes away and returns later, it will show up as a new opportunity.',
      reason: 'Reason',
      note: 'Note',
      noteHelp: 'Required if you choose Other.',
      submit: 'Dismiss',
      done: 'Opportunity dismissed.',
      errors: {
        OPPORTUNITY_NOT_OPEN: 'This opportunity is already closed. Someone may have just dismissed it, or waits are back to normal.',
        DISMISSAL_NOTE_REQUIRED: 'Please add a note when you choose Other.',
      },
      reasons: {
        EXPECTED_BEHAVIOUR: 'This was expected',
        KNOWN_OPERATIONAL_CONTEXT: 'We already know the reason',
        INSUFFICIENT_SIGNIFICANCE: 'Too small to matter',
        INCORRECT_INTERPRETATION: 'The figures don’t match what happened',
        INSUFFICIENT_DATA: 'Not enough data',
        DUPLICATE: 'Duplicate',
        NO_ACTION_REQUIRED: 'No action needed',
        OTHER: 'Other',
      },
    },
    detail: {
      back: 'Opportunities',
      period: 'Period: {range}',
      evidence: 'What the data shows',
      usual: 'Usual wait',
      usualHelp: 'The median wait over the {weeks} weeks before waits went up ({range}).',
      latest: 'Most recent longer-wait week',
      change: 'Change',
      chartLabel: 'Median wait each week, compared with the usual wait',
      weekOf: 'Week of {week}',
      weekTooltip: '{median}, {measured} visits measured',
      pointLabel: '{week}. {value}. {judgement}.',
      usualLine: 'Usual wait · {value}',
      judgement: {
        WORSE: 'Longer than usual',
        NORMAL: 'As usual',
        BASELINE: 'Before waits went up',
      },
      notJudged: 'Weeks with fewer than {minimum} measured visits aren’t shown: too few to judge.',
      breakdowns: {
        DAY: 'By day',
        HOUR: 'By hour',
        ROLE: 'By role',
        VISIT_TYPE: 'By visit type',
      },
      breakdownLabel: 'View by',
      columns: {
        DAY: 'Day',
        HOUR: 'Hour',
        ROLE: 'Professional role',
        VISIT_TYPE: 'Visit type',
        usual: 'Usual weeks',
        worse: 'Longer-wait weeks',
        change: 'Change',
      },
      visits: '{n} visits',
      byRoleHelp: 'Each visit counts for the role of the member who started it in ElfieCare. Individual names are never shown.',
      byVisitTypeHelp: 'A walk-in is a visit without an appointment. A first visit is a booked visit for a patient who is new to the clinic.',
      hidden: 'No group hidden | 1 group is hidden because it’s too small: fewer than {minimum} visits, or, for a role, fewer than {minimum} members. | {n} groups are hidden because they’re too small: fewer than {minimum} visits, or, for a role, fewer than {minimum} members.',
      noGroups: 'No group has enough visits to show.',
      whatWeKnow: 'What we know',
      claimClass: {
        DERIVED_FACT: 'Calculated from your data',
      },
      disclaimer: 'These figures show where waits went up, not why. Use them to decide where to look first.',
      appointments: {
        UP: 'Appointments went up, from {from} a week usually to {to} a week in the longer-wait weeks ({change}).',
        DOWN: 'Appointments went down, from {from} a week usually to {to} a week in the longer-wait weeks ({change}).',
        STEADY: 'Appointments stayed about the same, from {from} a week usually to {to} a week in the longer-wait weeks ({change}).',
      },
      method: 'Last checked on {date}.',
      notFound: 'We couldn’t find this opportunity in this workspace.',
      followUps: 'Follow-ups',
    },
    /** Fewer chronic patients with a next step (SIGNAL-CONT-NEXT-STEP-LOWER, DECISIONS.md #45). */
    nextStep: {
      howFound:
        'For chronic patients’ next steps, we take the end of each full week and compare the share of chronic patients seen in the last 90 days who have a next step with the {baselineWeeks} weeks before it began to fall. A week counts as lower when the share is at least {points} below usual, with at least {patients} chronic patients seen. It closes on its own after {resolveWeeks} weeks back at the usual level. This is a fixed rule, not AI.',
      /** Plural by `n`: none | one | more. */
      affected:
        'Every chronic patient seen in the last 90 days has a next step | 1 chronic patient seen in the last 90 days has no next step | {n} chronic patients seen in the last 90 days have no next step',
      resolved: 'Resolved on {date}: the share with a next step was back to usual.',
      detail: {
        usual: 'Usual share',
        usualHelp: 'The share at the end of the {weeks} weeks before it began to fall, taken together ({range}).',
        latest: 'Most recent lower week',
        chartLabel: 'Share of chronic patients with a next step at the end of each week, compared with the usual share',
        weekTooltip: '{share} of {recent} patients',
        usualLine: 'Usual share · {value}',
        judgement: {
          LOWER: 'Lower than usual',
          NORMAL: 'As usual',
          BASELINE: 'Before it began to fall',
        },
        notJudged: 'Weeks with fewer than {minimum} chronic patients seen aren’t shown: too few to judge.',
        patients: '{n} patients',
        withoutNextStep:
          'Chronic patients seen in the last 90 days with no next step: {latest} at the end of the most recent lower week, compared with {usual} usually.',
        followUpBooked:
          'Of chronic patients seen in the last 90 days, {latest} had a follow-up booked within 90 days of their last visit, compared with {usual} usually.',
        disclaimer: 'These figures show that fewer patients have a next step, not why. Use them to decide where to look first.',
      },
    },
  },
  /** P5, P6: the four takeaways, assign a follow-up, share with a note, set an objective, save a report. */
  actions: {
    takeaways: 'Next steps',
    origin: {
      OPPORTUNITY: 'Opportunity: {name}',
      OBJECTIVE: 'Objective: {name}',
      REPORT: 'Report: {name}',
    },
    /** A card's action to the opportunity, objective or report it follows up. */
    openOrigin: {
      OPPORTUNITY: 'Open the opportunity',
      OBJECTIVE: 'Open the objective',
      REPORT: 'Open the report',
    },
    priority: {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
      URGENT: 'Urgent',
    },
    form: {
      followsUp: 'Follows up {name}',
      choosePerson: 'Choose a colleague',
      choosePeople: 'Choose colleagues',
      noSeatYet: 'No Intelligence access yet',
      noSeatHelp: 'They’ll see it once the workspace Owner gives them Intelligence access.',
      colleaguesFailed: 'We couldn’t load your colleagues. Close this and try again.',
      clearDate: 'No date',
    },
    assign: {
      button: 'Assign',
      title: 'Assign a follow-up',
      taskTitle: 'What needs doing',
      assignee: 'Assign to',
      priority: 'Priority',
      dueDate: 'Due date',
      dueDateHelp: 'Optional. Without a due date, it’s never marked overdue.',
      note: 'Instructions',
      submit: 'Assign',
      done: 'Follow-up assigned.',
    },
    share: {
      button: 'Share',
      title: 'Share with a note',
      what: 'Sharing {name}',
      rule: 'Sharing keeps colleagues informed. It doesn’t give them a task.',
      recipients: 'Share with',
      note: 'Note',
      noteHelp: 'Optional. What you’d like them to notice.',
      submit: 'Share',
      done: 'Shared.',
    },
    objective: {
      button: 'Set as objective',
      done: 'Objective set.',
    },
    report: {
      button: 'Save',
      done: 'Saved. You’ll find it under Reports.',
      view: 'View the saved report',
    },
    /** P4 §C9.6, DECISIONS.md #32: the take-aways under an Elfie answer. */
    answer: {
      context: 'Elfie answer: “{question}”',
      savesFirst: 'To assign or share this answer, we save it as a report first, so your colleague sees exactly what you saw.',
      savesFirstLabel: 'What happens when you assign or share an answer',
    },
    task: {
      people: 'Assigned by {assigner} to {assignee}',
      noDueDate: 'No due date',
      due: 'Due {date}',
      overdueSince: 'Was due {date}',
      overdue: 'Overdue',
      completedBy: 'Completed by {name}',
      complete: 'Mark complete',
      start: 'Start',
      dismiss: 'Dismiss',
      reopen: 'Reopen',
      status: {
        OPEN: 'Open',
        IN_PROGRESS: 'In progress',
        COMPLETED: 'Completed',
        DISMISSED: 'Dismissed',
      },
      changed: {
        OPEN: 'Follow-up reopened.',
        IN_PROGRESS: 'Follow-up started.',
        COMPLETED: 'Follow-up completed.',
        DISMISSED: 'Follow-up dismissed.',
      },
    },
    shared: {
      by: 'Shared by {name} · {date}',
      unread: 'Not opened yet',
      open: 'View',
    },
    /** The API's error codes, in the reader's language: never its English message. */
    errors: {
      generic: 'That didn’t save. Please try again.',
      VALIDATION_FAILED: 'Some details are missing or not valid. Please check the form.',
      INTELLIGENCE_ACCESS_REQUIRED: 'You no longer have Intelligence access in this workspace.',
      ORIGIN_NOT_FOUND: 'What this follows up no longer exists.',
      OPPORTUNITY_NOT_FOUND: 'This opportunity no longer exists.',
      MEMBER_NOT_AVAILABLE: 'That colleague is no longer an active member of this workspace.',
      TASK_NOT_FOUND: 'This follow-up no longer exists.',
      TASK_CHANGE_NOT_ALLOWED: 'Only the person it’s assigned to, or the person who assigned it, can change this follow-up.',
      TASK_CHANGED: 'Someone else has just changed this follow-up. You’re now seeing the latest version.',
      TASK_DUE_DATE_PAST: 'Choose a due date from today onwards.',
      SHARE_NOT_FOUND: 'This share no longer exists.',
      SHARE_RECIPIENT_INVALID: 'Choose someone other than yourself to share with.',
      OBJECTIVE_NOT_FOUND: 'This objective no longer exists.',
      OBJECTIVE_NOT_MEASURABLE: 'There’s no figure for this measure in the last 30 days yet, so there’s nothing to set a target against.',
      OBJECTIVE_HISTORY_TOO_SHORT: 'Your records don’t cover the last 30 days yet, so there’s no starting point to set a target against.',
      OBJECTIVE_TARGET_INVALID: 'Choose a target different from the starting point. For waiting time, the target must be lower.',
      OBJECTIVE_DATE_INVALID: 'Choose a target date after today, up to two years ahead.',
      OBJECTIVE_METRIC_MISMATCH: 'An objective set from an opportunity uses the same measure as the opportunity.',
      OBJECTIVE_CHANGE_NOT_ALLOWED: 'Only the person who set this objective can change it.',
      OBJECTIVE_CLOSED: 'This objective has ended, achieved or missed, so it can’t be changed.',
      REPORT_NOT_FOUND: 'This report no longer exists.',
      REPORT_NO_RECORDS: 'Nothing is recorded in this workspace yet, so there’s nothing to put in a dossier.',
      PERIOD_INVALID: 'We can’t report on that period.',
    },
  },
  /** P5 §2: objectives on the Overview's five metrics. */
  objectives: {
    title: 'Objectives',
    subtitle: 'Targets your team is working towards, and how close you are.',
    create: 'Create objective',
    howMeasured:
      'An objective on one of the Overview’s figures looks at the last 30 days, the same way the Overview does. One on your patients’ care journey looks at the share as it stands. Each updates every time you open it.',
    saved: 'Objective saved.',
    reviewed: 'Marked as reviewed.',
    markReviewed: 'Mark reviewed',
    filter: {
      label: 'Show',
      ACTIVE: 'Active',
      ACHIEVED: 'Achieved',
      ALL: 'All',
    },
    empty: {
      ACTIVE: 'You’re not tracking any objectives. Create one, or set one from an opportunity.',
      ACHIEVED: 'No objectives achieved yet.',
      ALL: 'No objectives set in this workspace yet.',
    },
    /** S0 §38.1, DECISIONS.md #29. */
    status: {
      ON_TRACK: 'On track',
      AT_RISK: 'At risk',
      BEHIND: 'Behind',
      ACHIEVED: 'Achieved',
      MISSED: 'Missed',
    },
    card: {
      baseline: 'Starting point',
      target: 'Target',
      current: 'Current',
      atMost: '{value} or less',
      atLeast: '{value} or more',
      by: 'by {date}',
      noValue: 'Not available right now',
      lastThirtyDays: 'Last 30 days',
      /** A care journey share (DECISIONS.md #45): taken on one day, read as it stands. */
      asItStands: 'As it stands',
      onDay: 'on {date}',
      progress: '{progress} of the way to the target, with {time} of the time used.',
      notMeasurableNow: 'We can’t measure the last 30 days right now ({readiness}), so progress isn’t shown.',
      notMeasurableShare: 'We can’t measure this share right now ({readiness}), so progress isn’t shown.',
      /** Dao, 1 Oct 2026: judged once its own 30 days are in. */
      judgedFrom: 'Progress is judged from {date}, once 30 full days have passed since it was set.',
      /** Plural by `total`. */
      followUps: 'No follow-ups | 1 follow-up, {completed} completed | {total} follow-ups, {completed} completed',
      setBy: 'Set by {name} on {date}',
    },
    form: {
      title: 'Set an objective',
      editTitle: 'Change this objective',
      metric: 'Measure',
      chooseMetric: 'Choose a measure',
      baseline: 'Starting point: {value} over the last 30 days, as on the Overview today.',
      baselineSet: 'Starting point: {value}, when the objective was set.',
      notMeasurable: 'There’s no figure for this measure in the last 30 days yet, so you can’t set an objective on it.',
      /** A care journey share (DECISIONS.md #45). */
      baselineNow: 'Starting point: {value}, as your patients’ care journey stands today.',
      notMeasurableNow: 'There’s no figure for this step of the care journey yet, so you can’t set an objective on it.',
      name: 'Name',
      target: 'Target',
      targetMinutes: 'Target, in minutes',
      targetPercent: 'Target, in %',
      targetAtMostHundred: 'A share can’t be more than 100%.',
      targetIsBaseline: 'Choose a target different from the starting point.',
      targetMustFall: 'For waiting time, choose a target lower than the starting point.',
      targetMustRise: 'Choose a target higher than the starting point.',
      targetDate: 'Target date',
      targetDateHelp: 'We compare progress with the time left until this date.',
      submit: 'Set objective',
    },
  },
  /** P6 §3: My Attention. */
  attention: {
    title: 'My Attention',
    subtitle: 'Follow-ups, shared findings and objectives waiting for you.',
    viewObjective: 'Review objective',
    tabs: {
      label: 'Show',
      ALL: 'All ({n})',
      ASSIGNED: 'Assigned to me ({n})',
      SHARED: 'Shared with me ({n})',
      OBJECTIVES: 'Objectives to review ({n})',
    },
    sections: {
      overdue: 'Overdue ({n})',
      assigned: 'Assigned to me ({n})',
      shared: 'Shared with me ({n})',
      objectives: 'Objectives to review ({n})',
    },
    empty: {
      assigned: 'Nothing is assigned to you right now.',
      shared: 'Nothing has been shared with you.',
      objectives: 'None of your objectives needs attention right now.',
    },
  },
  /** P6 §4: saved reports and meeting dossiers. */
  reports: {
    title: 'Reports & meeting dossiers',
    subtitle: 'Snapshots of your figures for meetings and your records. Each one stays exactly as it was when it was created.',
    generateTitle: 'Meeting dossiers',
    generateHelp: 'A dossier captures your figures as they are right now, ready for a meeting. Nothing in it is written by AI.',
    generate: 'Generate',
    latest: 'Last generated {date}',
    viewLatest: 'View the latest',
    view: 'View',
    generatedBy: 'Generated by {name} · {date}',
    label: '{kind} · {period}',
    back: 'Reports',
    notFound: 'We couldn’t find this report in this workspace.',
    timezone: 'Days in {zone} time',
    fingerprint: 'Verification code: {value}',
    frozen: 'These are the figures as they were when this report was created. They won’t change.',
    exportCsv: 'Export CSV',
    print: 'Print or save as PDF',
    kinds: {
      TODAY: {
        name: 'Daily brief',
        about: 'Today so far, compared with the same day last week, plus follow-ups still open.',
      },
      WEEKLY: {
        name: 'Weekly operations review',
        about: 'Last full week, Monday to Sunday, compared with the week before, plus objectives and open follow-ups.',
      },
      MONTHLY: {
        name: 'Monthly review',
        about: 'Last full month, with your chronic patients’ care journey, objectives and follow-ups.',
      },
      EXECUTIVE: {
        name: 'Executive review',
        about: 'The last full quarter, for leadership and the board.',
      },
      OPPORTUNITY: {
        name: 'Opportunity report',
        about: 'One opportunity as you saw it, with what was done about it.',
      },
      ANSWER: {
        name: 'Elfie answer',
        about: 'One answer from Ask Elfie, as the person who asked saw it.',
      },
    },
    tabs: {
      label: 'Show',
      ALL: 'All',
      DOSSIERS: 'Meeting dossiers',
      SAVED: 'Saved reports',
      SHARED: 'Shared with me',
    },
    empty: {
      ALL: 'No reports in this workspace yet.',
      DOSSIERS: 'No meeting dossiers yet.',
      SAVED: 'No saved reports yet. Save one from an opportunity or an Elfie answer.',
      SHARED: 'No reports have been shared with you.',
    },
    sharedFailed: 'We couldn’t load what was shared with you. Please try again in a moment.',
    sections: {
      opportunities: 'Opportunities open in the period',
      objectives: 'Objectives',
      followUps: 'Follow-ups',
    },
    none: {
      opportunities: 'No opportunities were open in this period.',
      objectives: 'No objectives were being tracked.',
      followUps: 'No follow-ups were open or completed in this period.',
    },
    /** DECISIONS.md #39: the Overview's other perspective, frozen with the rest. */
    careQuality: {
      title: 'Care quality',
      /** A dossier saved before dossiers included it. */
      notIncluded: 'This dossier was saved before dossiers included Care quality.',
    },
    /** DECISIONS.md #32: a saved Elfie answer. */
    answer: {
      question: 'Question',
      title: 'Elfie’s answer',
    },
    csv: {
      report: 'Report',
      generated: 'Generated',
      timezone: 'Time zone',
      fingerprint: 'Verification code',
      metric: 'Measure',
      value: 'Value',
      compared: 'Compared with',
      readiness: 'How complete the data is',
      opportunities: 'Opportunities',
      opportunity: 'Opportunity',
      priority: 'Priority',
      status: 'Status',
      objectives: 'Objectives',
      followUps: 'Follow-ups',
      week: 'Week of',
      medianWait: 'Median waiting time',
      measured: 'Visits measured',
      /** An opportunity's usual level and its latest worse or lower week's, in its own unit. */
      usual: 'Usual',
      latest: 'Most recent week',
      nextStepShare: 'Share with a next step',
      chronicPatients: 'Chronic patients seen in the last 90 days',
      /** Beside a care-journey share: how many it is of. */
      outOf: 'Out of',
      statement: 'Statement',
      claimClass: 'Type of statement',
      notes: 'Notes',
      limits: 'What limits this answer',
    },
  },
  /** S0 §56.5, S4 §4c, DECISIONS.md #28: connect or collect, on the Investigation view (P3 §3.4). */
  coverage: {
    title: 'Connect more data',
    intro: 'ElfieCare’s records show that waits went up, but not why. Each source below would help explain it. Until one is connected, we don’t guess.',
    introNextStep: 'ElfieCare’s records show that fewer chronic patients have a next step, but not why. Each source below would help explain it. Until one is in place, we don’t guess.',
    inputs: {
      ARRIVAL_EVENTS: {
        name: 'Arrival and check-in times',
        without: 'Without them, we can’t see how long patients waited before someone was assigned to them.',
      },
      TRIAGE_EVENTS: {
        name: 'Triage steps',
        without: 'Without them, we can’t see whether triage held patients up.',
      },
      ROOM_CALL_EVENTS: {
        name: 'Call-in times',
        without: 'Without them, we can’t tell whether patients were waiting for a free room or for their clinician.',
      },
      /** DECISIONS.md #45: what would explain fewer next steps. */
      REMINDER_EVENTS: {
        name: 'Appointment reminders',
        without: 'Without them, we can’t tell whether patients were reminded of their next visit, or confirmed it.',
      },
      LAB_ORDERS: {
        name: 'Lab orders',
        without: 'Without them, a test still to be done doesn’t count as a next step, so some patients look as if they have none.',
      },
      STAFFING_ROSTERS: {
        name: 'Staff rosters',
        without: 'Without them, we can’t tell whether fewer clinicians were on shift. Longer waits tell us about patients, not about how busy staff were.',
      },
      /** DECISIONS.md #39: what the care journey and the guideline checks read. `needs` completes "Needs …". */
      CHRONIC_DIAGNOSES: {
        name: 'Coded diagnoses',
        without: 'Without them, we can’t tell which patients have diabetes, high blood pressure or kidney disease, so the journey can’t start.',
        needs: 'diagnoses coded with ICD-10',
      },
      MEDICATION_LISTS: {
        name: 'Medicine lists',
        without: 'Without them, we can’t tell whether a chronic patient’s treatment is on record.',
        needs: 'patients’ current medicines',
      },
      READINGS: {
        name: 'Readings and lab results',
        without: 'Without them, we can’t see whether patients are checked, or whether an abnormal result is followed up.',
        needs: 'readings or lab results',
      },
      FOLLOW_UP_BOOKINGS: {
        name: 'Booked appointments',
        without: 'Without them, we can’t see whether patients leave with their next visit booked.',
        needs: 'appointments booked in ElfieCare',
      },
      APP_CONNECTIONS: {
        name: 'Elfie app connections',
        without: 'Without them, we can’t see patients between visits.',
        needs: 'patients invited to the Elfie app',
      },
      CODED_KIDNEY_TESTS: {
        name: 'Coded kidney tests',
        without: 'ElfieCare stores lab tests by their written name, so we can’t reliably tell an eGFR or a urine albumin test from the others.',
        needs: 'kidney tests with their lab codes',
      },
      FOOT_EXAM_RECORDS: {
        name: 'Foot exam records',
        without: 'ElfieCare has no structured record of a foot exam, so we can’t count them.',
        needs: 'foot exams recorded as structured results',
      },
    },
    connect: {
      title: 'Connect what you already use',
      from: {
        ARRIVAL_EVENTS: 'From your hospital information system or check-in kiosk.',
        TRIAGE_EVENTS: 'From your triage or emergency system.',
        ROOM_CALL_EVENTS: 'From your queue display or calling system.',
        STAFFING_ROSTERS: 'From your rostering or HR system.',
        REMINDER_EVENTS: 'From your reminder or patient messaging service.',
        LAB_ORDERS: 'From your laboratory ordering system or your EHR’s order list.',
        CHRONIC_DIAGNOSES: 'From your EHR’s problem list or your billing diagnosis codes.',
        MEDICATION_LISTS: 'From your EHR’s medication list or your pharmacy’s dispensing records.',
        READINGS: 'From your laboratory system or your patients’ home devices.',
        FOLLOW_UP_BOOKINGS: 'From your hospital or clinic scheduling system.',
        APP_CONNECTIONS: 'From your patient portal or remote monitoring service.',
        CODED_KIDNEY_TESTS: 'From your laboratory system, with each test’s LOINC code.',
        FOOT_EXAM_RECORDS: 'From your EHR, where foot exams are recorded as structured results.',
      },
      action: 'See what’s needed',
    },
    collect: {
      title: 'Collect it with ElfieCare',
      /** How to record each input in ElfieCare, where ElfieCare can. */
      from: {
        CHRONIC_DIAGNOSES: 'Record each patient’s conditions with their ICD-10 code in their ElfieCare patient folder.',
        MEDICATION_LISTS: 'Record patients’ medicines in their ElfieCare patient folder.',
        READINGS: 'Record blood pressure, HbA1c and lab results in the patient folder at each visit.',
        FOLLOW_UP_BOOKINGS: 'Book the next appointment in ElfieCare before the patient leaves.',
        APP_CONNECTIONS: 'Invite chronic patients to the Elfie app from their patient folder.',
        REMINDER_EVENTS: 'Turn on appointment reminders in ElfieCare, so patients are reminded of their next visit and can confirm it.',
      },
      action: 'Open ElfieCare',
    },
    requirements: {
      title: 'What’s needed: {input}',
      fields: 'Fields',
      columns: {
        field: 'Field',
        holds: 'What it holds',
        required: 'Required',
      },
      required: 'Required',
      optional: 'Optional',
      /** How each kind of field is written, so the IT team sends what can be matched. */
      kinds: {
        ID: 'Identifier, as text',
        TIMESTAMP: 'Date and time with its UTC offset (ISO 8601), e.g. 2026-09-30T08:15:00+02:00',
        TEXT: 'Text',
        CODE: 'Your system’s own code, matched with Elfie when the connection is set up',
      },
      formats: 'Accepted formats',
      formatNames: {
        FHIR_R4: 'FHIR R4',
        REST_JSON: 'REST API (JSON)',
        CSV_BATCH_UPLOAD: 'CSV files sent in batches',
      },
      identifies: {
        PATIENTS: 'These records are linked to patients, so they come through a secure, managed connection and are protected like your patient records. They can’t be typed in by hand.',
        STAFF: 'These records name staff, so they come through a secure, managed connection. They don’t change the figures shown by member, which come only from each member’s own work in ElfieCare.',
      },
      setUp: 'Elfie sets up the connection with your IT team. Nothing is sent or uploaded from this page.',
      download: 'Download CSV template',
      copy: 'Copy for your IT team',
      copied: 'Copied. Paste it into a message to your IT team.',
      copyFailed: 'Your browser blocked copying. Select the text below and copy it instead.',
      copyHeading: 'Data requested by {workspace} for Elfie Care Intelligence: {input}',
      copyRequired: '- {column} (required): {holds}. Format: {format}',
      copyOptional: '- {column} (optional): {holds}. Format: {format}',
      copyFormats: 'Accepted formats: {formats}',
    },
    /** What each field a connection supplies holds (S4 §4c.1). */
    fields: {
      ENCOUNTER_ID: 'The visit it belongs to, as your system numbers it',
      PATIENT_REFERENCE: 'The patient, as your system identifies them, so each record can be matched to their ElfieCare patient folder',
      ARRIVED_AT: 'When the patient arrived',
      CHECKED_IN_AT: 'When the patient checked in',
      SITE: 'Where they arrived, if you have more than one site',
      TRIAGE_STARTED_AT: 'When triage started',
      TRIAGE_ENDED_AT: 'When triage ended',
      TRIAGE_LEVEL: 'The triage category given',
      CALLED_AT: 'When the patient was called in',
      ROOM: 'The room they were called to',
      STAFF_ID: 'The staff member, as your system identifies them',
      ROLE_TYPE: 'Their role on that shift',
      SHIFT_START: 'When the shift started',
      SHIFT_END: 'When the shift ended',
      SERVICE_LOCATION: 'Where they worked that shift',
      CONDITION_CODE: 'The diagnosis, as an ICD-10 code',
      RECORDED_AT: 'When the diagnosis was recorded',
      CLINICAL_STATUS: 'Whether the condition is active, in remission or resolved',
      MEDICATION_CODE: 'The medicine, as your system codes it',
      MEDICATION_STATUS: 'Whether the patient is still taking it',
      STARTED_AT: 'When the patient started it',
      STOPPED_AT: 'When the patient stopped it',
      TEST_CODE: 'The test, as your laboratory codes it (LOINC if you have it)',
      RESULT_VALUE: 'The result',
      MEASURED_AT: 'When it was measured',
      RESULT_UNIT: 'The result’s unit',
      ABNORMAL_FLAG: 'Whether your system marks the result abnormal',
      APPOINTMENT_ID: 'The appointment, as your system numbers it',
      SCHEDULED_FOR: 'When the appointment is for',
      BOOKED_AT: 'When it was booked',
      APPOINTMENT_STATUS: 'Whether it’s confirmed, cancelled or attended',
      CONSENTED_AT: 'When the patient agreed to share their readings',
      READING_TYPE: 'What was measured: blood pressure, glucose, weight or another reading',
      EXAMINED_AT: 'When the foot exam was done',
      EXAM_RESULT: 'What the exam found',
      SENT_AT: 'When the reminder was sent',
      DELIVERED_AT: 'When it reached the patient',
      CONFIRMED_AT: 'When the patient confirmed the appointment',
      ORDER_ID: 'The order, as your system numbers it',
      ORDERED_AT: 'When the test was ordered',
      DUE_BY: 'When the test should be done by',
    },
  },
  /** The dedicated Intelligence shell (P1 §4). */
  intelligence: {
    /** P1 §3.2: the spec's wording. */
    switchToOperations: 'Switch to Operations',
    switchToOperationsUnset: 'Switching to Operations isn’t available here yet: ElfieCare’s address isn’t set for this environment.',
    workspaceSwitcher: 'Switch workspace',
    /** The switcher's name: the workspace it shows, then what it does. */
    workspaceSwitcherCurrent: '{name}, switch workspace',
    currentWorkspace: 'Current workspace',
  },
  /** P4: the Conversational AI Analyst, its prompt bar and its answer panel. */
  /** P1 §6.1, S0 §18.0a: the reader's own management function, in Intelligence settings. */
  myFunction: {
    /** P1 §6.1. */
    question: 'What’s your role?',
    help: 'Elfie uses it to decide what to show you first.',
    promise: 'It only changes how Elfie presents findings to you. Figures, and who can see what, stay the same.',
    notYet: 'Until you choose, Elfie shows findings from the whole institution’s point of view.',
    saved: 'Saved. Your function: {function}.',
    functions: {
      EXECUTIVE_LEADERSHIP: { name: 'Executive leadership', who: 'CEO, hospital director' },
      OPERATIONS_MANAGEMENT: { name: 'Operations management', who: 'COO, clinic operations lead' },
      MEDICAL_LEADERSHIP: { name: 'Medical leadership', who: 'CMO, medical director, department head' },
      QUALITY_MANAGEMENT: { name: 'Quality management', who: 'Quality lead, accreditation officer' },
      PRACTICE_LEADERSHIP: { name: 'Practice leadership', who: 'Clinic owner, lead GP' },
    },
  },
  /** P4 §C7.2: Elfie Pulse, written from the figures by a fixed rule (Dao, 1 Oct 2026). */
  /** A clinic with no data yet, or still building its history (DECISIONS.md #34). */
  /** P1 §8, S0 §26b, DECISIONS.md #35: the clinic's profile and what its Owners and Admins declare about it. */
  clinicProfile: {
    mainConcern: 'Main concern',
    declared: 'About the workspace',
    declaredBadge: 'Self-reported',
    /** S0 §26b.5: never shown without who declared it and the day it describes. */
    marker: 'Reported by {name}, as of {date}',
    outOfDate: 'Reported a while ago, so it may have changed. Update it if needed.',
    setUp: 'Tell Elfie about your clinic',
    saved: 'Workspace profile saved. You can update it any time in settings.',
    /** Why a change was refused, by its error code. */
    errors: {
      CONTEXT_CHANGED: 'Someone else has just changed the workspace profile. You’re now seeing the latest version: go through it again to make your change.',
      CONTEXT_STATEMENT_NOT_FOUND: 'That’s no longer in the workspace profile. You’re now seeing the latest version.',
    },
    domains: {
      OPERATIONS: 'Run operations better',
      CARE_QUALITY: 'Deliver better care',
    },
    priorities: {
      WAITING_TIME: { name: 'Waiting times', about: 'Patients wait too long before they are seen.' },
      CONSULTATION_PACING: { name: 'Consultation pacing', about: 'Consultations run long, or unevenly through the day.' },
      NO_SHOWS: { name: 'No-shows', about: 'Patients miss the appointments they booked.' },
      CHRONIC_SCREENING: { name: 'Chronic care screening', about: 'Patients with a chronic condition miss the checks they are due.' },
    },
    facility: {
      CLINIC: 'Clinic',
      OUTPATIENT_CENTRE: 'Outpatient center',
      SPECIALTY_HOSPITAL: 'Specialty hospital',
      REGIONAL_NETWORK: 'Regional network',
    },
    software: {
      PAPER_OR_SPREADSHEETS: 'Paper or spreadsheets',
      HIS_OR_EHR: 'A hospital information system or EHR',
      MODULAR_TOOLS: 'Separate tools, such as booking or reminders',
    },
    figures: {
      weeklyVisits: 'About {n} visits a week',
      typicalWait: 'A typical wait of about {minutes} min',
    },
    form: {
      welcome: 'Welcome to ElfieCare Intelligence',
      title: 'Your workspace profile',
      step: 'Step {step} of {total}',
      priorityQuestion: 'What would you most like to improve?',
      priorityHelp: 'Elfie will start there: what it can show you now, and what it would need to show more.',
      clinicQuestion: 'Tell Elfie about your clinic',
      clinicHelp: 'Answer what you know and leave the rest blank.',
      facility: 'What kind of facility is it?',
      software: 'What do you keep your records in today?',
      weeklyVisits: 'Visits a week, roughly',
      typicalWait: 'Typical wait in minutes, roughly',
      figuresNote:
        'Rough figures are fine. They’re marked as self-reported wherever they appear, so no one mistakes them for measured figures. We never ask about individual patients or staff.',
      wholeNumber: 'Enter a whole number from {min} to {max}.',
      reviewQuestion: 'Check your answers',
      reviewHelp: 'They’re saved under your name, as of today. Nothing is saved until you confirm.',
      nothingDeclared: 'Nothing about the clinic itself, just its main concern.',
      withdrawing: 'Removed, because you left it blank',
      reviewNote: 'Owners and Admins can update this any time in Intelligence settings. Earlier answers are kept in its history.',
      skip: 'Not now',
      back: 'Back',
      next: 'Next',
      declare: 'Confirm',
    },
    /** Master PRD §6.3 step 5: written from what the clinic declared, never by a model. */
    brief: {
      title: 'Elfie’s first brief',
      subtitle: 'Based on what you told us. Nothing here is measured yet.',
      told: 'What you told Elfie',
      cannotTell: 'What we can’t tell yet',
      limits:
        'These are your own estimates, so we can’t check them or see how they change from day to day. Measured figures appear as soon as ElfieCare records your first visits.',
      renew: 'Some answers were given a while ago. Update them if things have changed.',
      update: 'Update the workspace profile',
      report: {
        both: 'You report about {visits} visits a week and a typical wait of about {minutes} minutes.',
        visits: 'You report about {visits} visits a week.',
        wait: 'You report a typical wait of about {minutes} minutes.',
      },
      measured: {
        waitingTime:
          'We’ll measure waiting time from ElfieCare’s waiting room, from when a patient is assigned to the clinician who sees them until their visit starts. It starts with your first visit there, and after five full weeks we’ll flag weeks when waits go up.',
        chronicCare:
          'We’ll follow your chronic patients’ journey from the conditions, medicines and readings recorded in ElfieCare: who needs ongoing care, who has a next step, who is connected between visits, who is followed over time, and whether abnormal results are followed up.',
      },
      unmeasured: {
        CONSULTATION_PACING: 'We can’t measure consultation pacing yet. It needs the start and end time of each consultation, and none of your connected systems records them.',
        NO_SHOWS: 'We can’t measure no-shows yet. ElfieCare doesn’t record missed appointments as no-shows, and no other connected system does either.',
      },
    },
    settings: {
      title: 'Your workspace profile',
      help: 'What your Owners and Admins have told Elfie about the clinic. Elfie uses it until ElfieCare has your own figures, and always marks it as self-reported.',
      prioritySet: 'Set by {name} on {date}',
      noPriority: 'No main concern chosen yet.',
      noContext: 'Nothing reported about the clinic yet.',
      never: 'Only information about the clinic goes here, never about individual patients or staff. Self-reported figures are never treated as measured.',
      withdraw: 'Remove',
      withdrawn: 'Removed. It’s kept in the profile’s history.',
      again: 'Update answers',
    },
    history: {
      title: 'History: one earlier answer | History: {n} earlier answers',
      replaced: 'replaced by {name} on {date}',
      withdrawn: 'removed by {name} on {date}',
    },
  },
  history: {
    stage: {
      NO_DATA: 'No data yet',
    },
    home: {
      noData: 'No data yet. Figures will appear once you start using ElfieCare.',
    },
    start: {
      title: 'Getting started',
      subtitle: '{workspace} has no appointments or visits in ElfieCare yet, so there’s nothing to show. Here’s what you’ll see once you start.',
      willShow: 'What you’ll see',
      fromFirstDay: 'From day one: appointments, visits, patients seen and activity across the week.',
      comparisons: 'Once two periods in a row are fully recorded: what changed between them.',
      opportunities: 'After {weeks} full weeks: opportunities, when waits rise above your usual level.',
      objectives: 'After 30 days: objectives, measured over the last 30 days.',
      inElfieCare: 'Start in ElfieCare',
      record: 'Book appointments, check patients in through the waiting room and complete visits in ElfieCare. Each one shows up here the same day.',
      elsewhere: 'Already keep records in another system?',
      connect: 'Records in a hospital information or scheduling system could come in through a secure connection set up with your IT team. This isn’t available yet.',
      collect: 'Start recording them in ElfieCare. Each one shows up here as soon as it’s recorded.',
      openElfieCare: 'Open ElfieCare',
    },
    building: {
      title: 'Building your clinic’s history',
      since: 'Records since {day}',
      weeks: '{done} of {of} weeks',
      readyNow: 'Ready now',
      counts: 'Figures for any period since then, activity across the week and the most active members.',
      toCome: 'Still to come',
      comparisons: 'Comparisons with the previous period, once that period is fully recorded.',
      opportunities: 'Opportunities, from {day}, after {weeks} full weeks of records.',
      objectives: 'Objectives, from {day}, once you have 30 days of records.',
    },
    opportunities: {
      noData: 'Opportunities appear after {weeks} full weeks of records in ElfieCare. Nothing is recorded yet.',
      building: 'Opportunities appear after {weeks} full weeks of records. You have {done} so far, so the first could appear from {day}.',
    },
    objectives: {
      noData: 'You can set objectives once you have 30 days of records in ElfieCare. Nothing is recorded yet.',
      from: 'You can set objectives from {day}, once you have 30 days of records.',
    },
    reports: {
      noData: 'You can create dossiers once something is recorded in ElfieCare.',
    },
  },
  pulse: {
    title: 'Elfie Pulse',
    range: 'Briefing for {range}',
    investigate: 'Investigate',
    /** Two words, bold; the question Elfie is handed opens with it (Dao, 2 Oct 2026). */
    ask: 'Ask Elfie',
    quiet: 'Nothing stands out in this period.',
    /** DECISIONS.md #39: how much of the care journey can be measured. */
    continuity: {
      notConnected: 'Your chronic patients’ journey can’t be measured yet: ElfieCare’s clinical records aren’t connected to Intelligence.',
      /**
       * No step has a figure, with the reason every step shares; `total` is how many steps there are. OTHER where
       * their reasons differ or aren't known yet. TOO_FEW never says how many (S0 §17.1).
       */
      none: {
        INPUTS_NOT_RECORDED: 'None of the {total} steps of your chronic patients’ journey can be measured yet: your records don’t hold what they need.',
        TOO_FEW: 'None of the {total} steps of your chronic patients’ journey can be measured yet: there are still too few to give a reliable figure.',
        NOT_RECORDED: 'None of the {total} steps of your chronic patients’ journey can be measured for this period: your records start on {day}.',
        OTHER: 'None of the {total} steps of your chronic patients’ journey can be measured yet.',
      },
      /** Plural by `n`, the steps that can't be measured: none | one | more. `total` is how many steps there are. */
      partly:
        'Every step of your chronic patients’ journey can be measured. | 1 of the {total} steps of your chronic patients’ journey can’t be measured yet. | {n} of the {total} steps of your chronic patients’ journey can’t be measured yet.',
      open: 'See Care quality',
    },
    /** S3 §5.3.1 v2: why the headline leads (Dao, 2 Oct 2026). */
    basis: {
      objective: {
        ON_TRACK: 'First because of your objective “{title}”, on track.',
        AT_RISK: 'First because your objective “{title}” is at risk.',
        BEHIND: 'First because your objective “{title}” is behind.',
      },
      CONCERN: 'First because it’s your clinic’s main concern.',
      IMPACT: 'First because it’s the biggest change in this period.',
    },
    /** DECISIONS.md #35: the clinic's declared main concern, where no metric measures it. */
    concern: {
      CONSULTATION_PACING: 'Consultation pacing, your clinic’s main concern, can’t be measured yet: no connected system records when consultations start and end.',
      NO_SHOWS: 'No-shows, your clinic’s main concern, can’t be measured yet: ElfieCare doesn’t record missed appointments as no-shows.',
    },
    how: 'Written from the figures on this page by a fixed rule, not by AI.',
    howLabel: 'How this briefing is written',
    failed: 'Elfie Pulse didn’t load. The figures below are unaffected.',
    /** The card's sections and its actions (Dao, 1 Oct 2026: every card names its sections and boxes its actions at its end). */
    sections: {
      standsOut: 'What stands out',
      steady: 'Held steady',
      notMeasured: 'Not measured yet',
      waiting: 'Waiting for you',
    },
    actions: {
      /** Plural by `n`, the open opportunities: none | one | more. */
      opportunities: 'No open opportunities | See the open opportunity | See all {n} open opportunities',
      attention: 'Open My Attention',
    },
    attention: {
      /** Plural by `n`: none | one | more. */
      assigned: 'No follow-ups | 1 follow-up | {n} follow-ups',
      overdue: 'None overdue | 1 overdue | {n} overdue',
      unreadShares: 'Nothing shared with you | 1 shared with you | {n} shared with you',
      objectives: 'No objectives to review | 1 objective to review | {n} objectives to review',
      none: 'Nothing is waiting for you.',
    },
  },
  analyst: {
    /** P4 v3.0: the Copilot is Elfie, and this is how a reader asks it (Dao, 1 Oct 2026). */
    name: 'Ask Elfie',
    /** P4 §C4.1: whose view Elfie answers from, and the one time it asks for the reader's function. */
    perspective: {
      tailored: 'Tailored to your function: {function}.',
      change: 'Change',
      ask: 'Tell Elfie your role, and it will put what matters most to you first. Until then, it answers from the institution’s point of view.',
      set: 'Set my function',
      notNow: 'Not now',
      institution: 'Elfie answers from the institution’s point of view.',
    },
    /**
     * Elfie as a companion in the corner (Dao, 1 Oct 2026): what its bubbles say, from the workspace's own figures
     * where it has them. Each asks a question the data answers today.
     */
    companion: {
      /** Elfie introducing itself (Dao, 5 Oct 2026): who it is, then what it can do. */
      hello: 'Hi, I’m Elfie. You can ask me anything about your clinic.',
      abilities: 'I can help you analyze waiting times, visits and how your chronic patients are followed, from your clinic’s own records.',
      checked: 'I check every number against your data before I answer, and I tell you when something can’t be measured.',
      /** After the open waiting-time opportunity's figures for its week. */
      why: 'Want to look into why?',
      /** After the opportunity's sentence saying where the increase is concentrated. */
      byAxis: {
        HOUR: 'Want to see waiting time hour by hour?',
        DAY: 'Want to compare waiting time by day of the week?',
        ROLE: 'Want to compare waiting time by professional role?',
        VISIT_TYPE: 'Want to compare waiting time by visit type?',
      },
      /** After the open next-step opportunity's figures for its week (DECISIONS.md #45). */
      nextStep: 'Want to see how it has changed?',
      objective: 'Want to see how the measure behind “{title}” has moved?',
      compare: 'Want to compare with the period before?',
      /** DECISIONS.md #39: Elfie answers from the care journey too. */
      chronicCare: 'Want to see how your chronic patients are followed?',
      patientsSeen: 'Want to know how many patients were seen?',
      hide: 'Hide suggestions',
    },
    bar: {
      /** P4 §2: the spec's wording. */
      placeholder: 'Ask anything about {workspace}…',
      followUp: 'Ask a follow-up question…',
      send: 'Send',
      suggestions: 'Suggestions',
    },
    /** Only questions that get an answer from the data today (P4 v1.5). */
    suggestions: {
      overview: {
        waitingTime: 'Why did waiting time change?',
        comparePrevious: 'Compare with the period before',
        patientsSeen: 'How many patients were seen?',
        chronicCare: 'How is our chronic patients’ care journey going?',
      },
      /** From a Care Quality & Continuity measure Elfie Pulse leads with: its name, as the Overview gives it. */
      careMeasure: 'How has “{measure}” changed?',
      /** Waiting time by one of the breakdowns the engine computes (S1 Step 2). */
      byAxis: {
        HOUR: 'Compare waiting time by hour of day',
        DAY: 'Compare waiting time by day of the week',
        ROLE: 'Compare waiting time by professional role',
        VISIT_TYPE: 'Compare waiting time by visit type',
      },
      /** How one measure moved over the page's period, against the period before. */
      metric: {
        appointments: 'How did appointments change?',
        visitsCompleted: 'How did visits completed change?',
        patientsSeen: 'How did the number of patients seen change?',
        waitingTime: 'How did waiting time change?',
        activeMembers: 'How did the number of active members change?',
      },
    },
    disclosure: {
      /** P4 §5.3 (v3.5): under every question box; it names the provider and the region. */
      howItWorks: 'How this works',
    },
    unavailable: {
      title: 'Elfie can’t answer questions in this workspace.',
    },
    howItWorks: {
      title: 'How your questions are answered',
      where: 'Answered by Google’s Gemini API, which may run outside your country. Your data isn’t kept or used to train models.',
      sentTitle: 'What Elfie shares with the AI model',
      sent: {
        question: 'Your question, and the ones before it in this conversation',
        function: 'Your function, if you’ve set one, so the answer is written for you',
        results: 'Totals, medians, shares and changes already calculated for this workspace, with how many cases each covers',
        definitions: 'What each measure means, and the period it covers',
        readiness: 'How reliable each figure is',
        knowledge: 'General knowledge about ElfieCare and running a clinic',
      },
      neverTitle: 'What is never shared',
      never: {
        patients: 'Patient records, or anything that identifies a patient',
        members: 'Members’ names',
        clinical: 'Clinical notes, transcripts or other clinical text',
        small: 'Figures about any group smaller than {minimum}',
      },
      numbers:
        'The AI never calculates figures itself. We calculate them first, then check every number in the answer against our own before you see it. If an answer doesn’t pass, it isn’t shown.',
      record:
        'Each question is logged under Data & privacy: who asked, when, and what kind of information was shared. The question and the answer themselves aren’t kept there.',
    },
    panel: {
      newConversation: 'New conversation',
      close: 'Close',
      you: 'You',
    },
    /** The engine's steps, as the reader follows them (P4 v1.5: the words come after the check). */
    stages: {
      READING: 'Reading your question…',
      COMPUTING: 'Calculating: {metrics}…',
      COMPUTING_PERIOD: 'Calculating: {metrics}, {range}…',
      COMPUTING_ANY: 'Calculating the figures…',
      WRITING: 'Writing the answer…',
      REWRITING: 'The first draft didn’t pass the number check. Rewriting…',
      CHECKING: 'Checking the numbers…',
    },
    /** S0 §6, in words. */
    claims: {
      DERIVED_FACT: 'Calculated fact',
      ASSOCIATION: 'Pattern',
      DIAGNOSTIC_HYPOTHESIS: 'Possible explanation',
    },
    notices: {
      /** P4 §5.1 (v3.4): on every association, in view. */
      ASSOCIATION_NOT_CAUSATION: 'A pattern, not proof of the cause.',
      NEEDS_VALIDATION: 'A possible explanation to check, not a confirmed cause.',
      /** The ⓘ beside a claim label, for what the label means. */
      help: 'What this means',
    },
    sample: 'Based on {n} cases',
    statistic: 'Correlation {value}',
    verified: {
      label: 'Verified',
      text: 'Every number was checked against our own calculations.',
    },
    /** Beside the Demonstration data marker, where no shield is drawn (P4 §5.2). */
    notVerified: 'These figures are made up for demos, so they aren’t checked against real data.',
    /** P4 §5.2 (v3.4): never shown without who reported it and the day it describes (S0 §26b.5). */
    attested: 'Self-reported by {name} · as of {date} · not measured',
    outcomes: {
      NOT_MEASURABLE: 'Elfie can’t measure this yet.',
      CLINICAL_QUESTION:
        'This is a question about a patient’s care. Elfie looks at how your workspace runs, not at individual patients. For clinical guidance during a visit, use Evidence AI in ElfieCare.',
      ABOUT_A_PERSON:
        'Elfie reports on the workspace as a whole and by professional role, never on one named person.',
      OUT_OF_SCOPE:
        'Elfie can help with how this workspace runs: appointments, visits, waiting times, care quality and how members use ElfieCare.',
      WITHHELD:
        'Elfie’s explanation didn’t pass the number check, so it’s hidden. The figures below are our own calculations.',
    },
    /** S1 Step 7 check 3: every metric short of READY says so (S0 §21.1). */
    readiness: {
      PARTIAL: '{metric} is shown with some limits.',
      INSUFFICIENT: 'There’s too little data on {metric} to draw a conclusion.',
      STALE: '{metric} may be out of date.',
      UNAVAILABLE: '{metric} isn’t available right now.',
    },
    /** The limits the Overview states with their numbers, said here without them. */
    limits: {
      TOO_FEW: 'Too few measured cases for a reliable figure.',
      WAITING_ROOM_VISITS_ONLY: 'Only covers visits that went through the waiting room.',
      NOT_RECORDED_BEFORE: 'Only counts the days since your records started, partway through this period.',
      NOT_RECORDED: 'Nothing is recorded for this period.',
      /** A care-journey measure whose look-back reaches back before the records (DECISIONS.md #39). */
      LOOKBACK_NOT_RECORDED: 'Looks back further than your records go, so it only counts what’s been recorded since they started.',
    },
    notMeasurable: {
      NOT_REGISTERED: '{subject}: the data exists, but we don’t measure it yet.',
      NO_SOURCE: '{subject}: none of your connected systems records it.',
      TOO_FEW: '{subject}: too few cases in this period to say anything reliable.',
      SOURCE_UNAVAILABLE: '{subject}: the data can’t be reached right now.',
      NOT_RECORDED: '{subject}: nothing is recorded for this period.',
      /** What would make it measurable: a list of `sources` below. */
      needs: 'It needs {sources}.',
      /** Under an answer with findings: what is left out, in one line; the detail is on hover. Plural by `n`. */
      partly: 'Every part of your question was answered | 1 part of your question can’t be measured yet | {n} parts of your question can’t be measured yet',
      which: 'What couldn’t be measured',
    },
    /** What people ask about that no registered metric answers yet (the engine's UNMEASURED_CONCEPT_IDS). */
    concepts: {
      ARRIVAL_AND_CHECK_IN: 'Arrival and check-in',
      TRIAGE: 'Triage',
      ROOM_CALL: 'Being called in',
      CONSULTATION_DURATION: 'How long consultations last',
      SCRIBE: 'Visit Scribe',
      EVIDENCE_AI: 'Evidence AI use',
      REMINDERS: 'Appointment reminders',
      PREVISIT: 'Pre-visit summaries',
      ELFIE_APP: 'Patients’ use of the Elfie app',
      NO_SHOWS: 'No-shows',
      CANCELLATION_REASONS: 'Why appointments are cancelled',
      STAFFING: 'Staffing',
      DEPARTMENTS: 'Departments',
      KIDNEY_AND_FOOT_CHECKS: 'Kidney checks and diabetic foot exams',
      /** Answers saved before Elfie answered from Care Quality & Continuity (DECISIONS.md #39) name it so. */
      CARE_QUALITY: 'Care quality',
    },
    /** S3's analytics methods, when one cannot run (the engine's ANALYTICS_METHOD_IDS). */
    methods: {
      PERIOD_DELTA: 'The change against the period before',
      TIME_SERIES_MEDIAN_DELTA: 'The change in the median against the period before',
      IQR_SPREAD: 'How spread out the cases are',
      GROUP_BREAKDOWN: 'A breakdown by group',
      FUNNEL_CONVERSION: 'Stage-by-stage progression',
      MULTIVARIATE_REGRESSION: 'Several factors at once',
      SURVIVAL_LAG_ANALYSIS: 'The time until a visit note is finalized',
      COHORT_DECOMPOSITION: 'A breakdown of a care-quality indicator',
    },
    /** What would have to be recorded or connected (the engine's MISSING_SOURCES). */
    sources: {
      ARRIVAL_EVENTS: 'the time each patient arrives',
      TRIAGE_EVENTS: 'the triage steps',
      ROOM_CALL_EVENTS: 'the time each patient is called in',
      PREVISIT_EVENTS: 'the pre-visit summaries',
      SCRIBE_EVENTS: 'Visit Scribe activity',
      EVIDENCE_AI_EVENTS: 'Evidence AI activity',
      REMINDER_EVENTS: 'the reminders sent and delivered',
      ELFIE_APP_EVENTS: 'patients’ activity in the Elfie app',
      NO_SHOW_RECORDS: 'a record of patients who did not come',
      CANCELLATION_DETAILS: 'when and why appointments are cancelled',
      STAFFING_ROSTERS: 'staff rosters',
      DEPARTMENT_ATTRIBUTION: 'which department each record belongs to',
      CLINICAL_INDICATORS: 'care-quality measures set up for this workspace',
    },
    /** One group of a breakdown, beside its number. Hours and weekdays are written by Intl. */
    groups: {
      visitType: {
        WALK_IN: 'Walk-in',
        FIRST_VISIT: 'First visit',
        RETURNING_PATIENT: 'Returning patient',
      },
      /** ElfieCare's own labels for `User.professionalRole`. */
      role: {
        UNKNOWN: 'No professional role on record',
      },
    },
    suppressed: 'Groups smaller than {minimum} cases are hidden to protect privacy ({groups} hidden).',
    numbers: {
      title: 'Figures used',
      grouped: '{label} · {group}',
      measure: {
        VALUE: '{metric}, {range}',
        BASELINE: '{metric}, {range}',
        SHARE: '{metric} as a share, {range}',
        CHANGE_ABSOLUTE: '{metric}, change',
        CHANGE_RELATIVE: '{metric}, change',
        P25: '{metric}, 25th percentile, {range}',
        P50: '{metric}, median, {range}',
        P75: '{metric}, 75th percentile, {range}',
        IQR: '{metric}, spread between the 25th and 75th percentiles, {range}',
        PEARSON_R: '{metric} against {covariate}, day by day, {range}',
      },
      covariates: {
        VISITS_STARTED_PER_DAY: 'visits started per day',
      },
    },
    errors: {
      ANALYST_DISABLED: 'Elfie can’t answer questions in this workspace right now.',
      LLM_UNAVAILABLE: 'Elfie couldn’t be reached. Please try again in a moment.',
      LLM_INVALID_OUTPUT: 'Elfie’s answer couldn’t be used. Please try asking again.',
      NETWORK_ERROR: 'The connection dropped before the answer arrived. Please try again.',
      STREAM_ENDED: 'The answer stopped before it finished. Please try again.',
    },
  },
  transparency: {
    description:
      'What happened in this workspace’s Intelligence, who did it and when, newest first. Nothing in it is ever edited or deleted, and it stays even if Intelligence is turned off.',
    exportCsv: 'Export CSV',
    exportJson: 'Export JSON',
    reason: 'Reason: {reason}',
    record: {
      title: 'Activity log',
      empty: 'Nothing logged yet.',
      more: 'Show older',
      system: 'System',
      fromElfie: '{name} (Elfie)',
      events: {
        enabledDemonstration: 'Intelligence turned on, with demonstration data',
        enabledOperational: 'Intelligence turned on, with this institution’s own data',
        enabledAgain: 'Intelligence turned back on',
        disabled: 'Intelligence turned off',
        suspended: 'Intelligence paused',
        accessGranted: '{member} given access to Intelligence',
        accessRevoked: '{member} no longer has access to Intelligence',
        /** An arrangement made on Vertex AI before DECISIONS.md #41: history, so it keeps its own words. */
        inferenceSet: 'AI set to run on Google Vertex AI in {region}. Data isn’t kept or used for training (agreement: {dpa})',
        inferenceSetGeminiApi:
          'AI set to run on Google’s Gemini API, which may run outside your country, with your institution’s consent. Data isn’t kept or used for training (agreement: {dpa})',
        inferenceRemoved: 'AI arrangement removed: no AI can use this workspace’s data',
        analystQuestion: 'Question to Elfie about {subject}: {outcome}',
        analystQuestionTurnedAway: 'Question to Elfie: {outcome}',
        /** P3 §2.7: one entry per system check, naming what it did and hid. */
        signalChecked: 'Checked {metric} for the weeks of {first} to {last}: {results}',
        signalCheckedOneWeek: 'Checked {metric} for the week of {first}: {results}',
        signalCheckedUnread: 'Checked this workspace’s data for opportunities',
      },
      question: {
        outcomes: {
          ANSWERED: 'answered, with every number checked',
          WITHHELD: 'explanation hidden, as it didn’t pass the number check',
          NOT_MEASURABLE: 'couldn’t be measured with the data connected today',
          CLINICAL_QUESTION: 'not answered: it was about a patient’s care',
          ABOUT_A_PERSON: 'not answered: it was about one person',
          OUT_OF_SCOPE: 'not answered: it wasn’t about this workspace',
          FAILED: 'stopped by an error',
        },
        /** A question answered on Vertex AI before DECISIONS.md #41. */
        sent: 'Shared with Google Vertex AI in {region}: {classes}. Never a patient record or anyone’s name.',
        sentGeminiApi: 'Shared with Google’s Gemini API: {classes}. Never a patient record or anyone’s name.',
        classes: {
          QUESTION: 'the question',
          AGGREGATE_RESULTS: 'calculated totals',
          METRIC_DEFINITIONS: 'what each measure means',
          KNOWLEDGE_OBJECTS: 'general operations knowledge',
          READINESS: 'how reliable the figures are',
          SCOPE_METADATA: 'the period asked about',
        },
        hidden: 'One group too small to show was left out. | {n} groups too small to show were left out.',
      },
      check: {
        metrics: {
          'METRIC-OP-WAITING-TIME': 'waiting time',
          'METRIC-CONT-CAREPLAN-ACT': 'the share of chronic patients with a next step',
        },
        created: '1 new opportunity | {n} new opportunities',
        updated: '1 updated | {n} updated',
        resolved: '1 resolved | {n} resolved',
        reopened: '1 reopened | {n} reopened',
        nothing: 'nothing new',
        hidden: '1 small group hidden (under {minimum}) | {n} small groups hidden (under {minimum})',
      },
      reasons: {
        NO_LONGER_AN_ACTIVE_MEMBER_IN_ELFIECARE: 'No longer an active member of the workspace in ElfieCare',
      },
    },
  },
  inference: {
    title: 'AI',
    permitted:
      'Google’s Gemini API, which may run outside your country: your institution agreed to that. Your data isn’t kept or used for training, confirmed by {name} on {date} (agreement: {dpa}). Any AI used for this workspace uses only this service.',
    noAi: 'No AI can use this workspace’s data.',
    setByElfie: 'Set up by Elfie under your data processing agreement. To change it, contact Elfie.',
    status: {
      PERMITTED: 'AI is allowed.',
      NOT_SET: 'AI isn’t set up for this workspace.',
      NO_ROUTE: 'No AI service is set up here yet.',
      ROUTE_CHANGED: 'The AI service has changed since this was agreed, so it needs agreeing again.',
    },
    admin: {
      title: 'AI arrangement: {name}',
      environment: 'This environment',
      route: {
        geminiApi: 'Google’s Gemini API. It doesn’t state a region, so it may run outside the institution’s country.',
        NOT_CONFIGURED: 'No Gemini API key is set for this environment.',
      },
      country: 'Institution’s country',
      countryUnknown: 'Not known in ElfieCare',
      countryInEu: '{country}, in the EU/EEA',
      current: 'Now',
      dpa: 'Data processing agreement reference',
      dpaHelp: 'The institution’s agreement that names Google as sub-processor and records its consent.',
      terms: 'I confirm the contract gives zero retention and no training use of this institution’s data.',
      consent:
        'I confirm the institution has given informed, recorded consent to its data being processed outside its country, and that its law allows it.',
      save: 'Set arrangement',
      remove: 'Remove arrangement',
      saved: 'AI arrangement set for {name}.',
      removed: 'AI arrangement removed for {name}.',
    },
  },
  workspaceAdmin: {
    title: 'Workspaces',
    description:
      'The ElfieCare workspaces Intelligence is switched on for. Only Elfie switches Intelligence on, off or into suspension, and each change goes on the institution’s transparency record.',
    switchOn: 'Switch on Intelligence',
    syncNow: 'Sync now',
    synced: 'Members are up to date with ElfieCare.',
    notConnected: {
      title: 'Not connected to ElfieCare yet',
      body: 'No workspace can be switched on until the read-only ElfieCare connection (ELFIECARE_REPLICA_URL) is set up for this environment.',
    },
    empty: 'Intelligence is not switched on for any workspace yet.',
    columns: {
      name: 'Workspace',
      mode: 'Data',
      status: 'Intelligence',
      members: 'Members',
      syncedAt: 'Last copied',
      ai: 'AI',
    },
    modes: {
      DEMONSTRATION: 'Demonstration',
      OPERATIONAL: 'Operational',
    },
    statuses: {
      ENABLED: 'On',
      DISABLED: 'Switched off',
      SUSPENDED: 'Suspended',
    },
    aiStatuses: {
      PERMITTED: 'Permitted',
      NOT_SET: 'Not set',
      NO_ROUTE: 'No route',
      ROUTE_CHANGED: 'Route changed',
    },
    closedInElfieCare: 'Closed in ElfieCare',
    syncProblems: 'The last copy had problems:',
    actions: {
      switchOff: 'Switch off',
      suspend: 'Suspend',
      switchBackOn: 'Switch back on',
      ai: 'AI arrangement',
    },
    switchOnForm: {
      workspaceId: 'ElfieCare workspace ID',
      workspaceIdHelp: 'The ID ElfieCare shows for the workspace.',
      mode: 'Data',
      modeHelp: {
        DEMONSTRATION:
          'Synthetic data for demonstrations and onboarding. Every page of the workspace is marked "Demonstration data".',
        OPERATIONAL: 'A real institution, with its data from ElfieCare.',
      },
      modeFixed: 'This cannot be changed later.',
      submit: 'Switch on',
      success: 'Intelligence is on for {name}.',
    },
    statusForm: {
      titleDisable: 'Switch off Intelligence for {name}?',
      titleSuspend: 'Suspend Intelligence for {name}?',
      titleEnable: 'Switch Intelligence back on for {name}?',
      bodyOff:
        'Nobody in the workspace can enter Intelligence until it is switched back on. The change and your reason go on the institution’s transparency record.',
      bodyOn: 'The workspace’s members are copied from ElfieCare again, and the Owner can enter.',
      reason: 'Reason',
      reasonHelp: 'The institution can read it.',
      success: '{name} updated.',
    },
  },
  languages: {
    title: 'Language management',
    description:
      'Every string in the product, in every language. English (US) ships with the app and is the reference; the others live here and can be edited in place.',
    search: 'Search keys or text',
    filters: {
      all: 'All',
      missing: 'Missing',
      machine: 'Machine translated',
      human: 'Edited',
    },
    coverage: '{translated} of {total} translated',
    humanEdits: '{count} edited by hand',
    columns: {
      key: 'Key',
      english: 'English (US)',
      value: 'Translation',
      source: 'Source',
    },
    sources: {
      missing: 'Missing',
      machine: 'Machine',
      human: 'Edited',
    },
    placeholder: 'Falls back to English until translated',
    editHint: 'Enter saves, Escape cancels',
    reset: 'Reset',
    resetConfirm: 'Remove this translation? The English text will show until a new one is entered.',
    saved: 'Saved.',
    empty: 'No strings match.',
    showing: 'Showing {shown} of {total}',
  },
  login: {
    welcome: 'Welcome to',
    emailLabel: 'Email address',
    emailPlaceholder: "your{'@'}email.com",
    submit: 'Email me a sign-in link',
    sending: 'Sending…',
    invalidEmail: 'Please enter a valid email address.',
    checkEmail: 'Check your inbox. We’ve sent you a 6-digit code and a sign-in link.',
    codeLabel: 'Sign-in code',
    codePlaceholder: '000000',
    codeHint: 'The 6 digits from the email.',
    codeSubmit: 'Sign in',
    invalidCode: 'Enter the 6-digit code from your email.',
    resend: 'Didn’t get it? Send it again.',
    backToLogin: 'Back to sign-in',
    verifying: 'Signing you in…',
    linkInvalid: 'This sign-in link has expired or isn’t valid. Please request a new one.',
  },
  users: {
    editUser: 'Edit user',
    updatedSuccess: '{name} updated.',
    emailImmutable: 'The email address identifies the account and cannot be changed.',
    empty: 'No users yet.',
    title: 'User management',
    add: 'Add user',
    name: 'Name',
    email: 'Email',
    function: 'Function',
    preferredName: 'Preferred name',
    preferredNameHelp: 'How the product greets them. Leave empty to use the full name.',
    role: 'Role',
    status: 'Status',
    created: 'Added',
    roles: {
      user: 'User',
      manager: 'Manager',
      admin: 'Admin',
    },
    statuses: {
      active: 'Active',
      inactive: 'Inactive',
    },
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    deactivateConfirm: 'Deactivate {name}? They will be signed out immediately.',
    addedSuccess: '{name} can now sign in.',
    fromElfieCare: 'ElfieCare',
    elfieCareManaged: 'Their name and status come from their ElfieCare account. Change them in ElfieCare.',
    sensitiveRoleWarning:
      'Managers and admins can view full consultation transcripts. Transcripts are de-identified, but grant these roles only to people who need that access.',
  },
} as const
